import { cn } from "../../../utils/cn.js";
import * as React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Toggle } from "@base-ui/react/toggle";
import { ToggleGroup as ToggleGroup$1 } from "@base-ui/react/toggle-group";
//#region src/components/ToggleGroup/ToggleGroup.tsx
const ToggleGroupVariantContext = React.createContext("default");
/**
* Curri-styled toggle group built on Base UI primitives.
*
* The default variant is a sunken-surface segmented control. `pill` is a
* wrapping row of independent chips, used for multi-select filters.
* Only one item can be pressed at a time by default (single-select).
* Supports both controlled and uncontrolled usage via `value` / `defaultValue`.
*
* @param props - Toggle group configuration and Base UI props
* @returns The rendered toggle group container
*/
function ToggleGroup({ className, variant = "default", ...props }) {
	return /* @__PURE__ */ jsx(ToggleGroupVariantContext.Provider, {
		value: variant,
		children: /* @__PURE__ */ jsx(ToggleGroup$1, {
			className: cn("inline-flex items-center font-body", variant === "default" && "gap-xs rounded-[var(--radius-xs-sm)] bg-elevation-surface-sunken p-xxxs", variant === "pill" && "flex-wrap gap-sm", "data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed", variant === "default" && "data-[disabled]:bg-background-disabled", className),
			...props
		})
	});
}
/**
* Individual toggle item within a ToggleGroup.
*
* Renders a button with an optional icon above a text label. When pressed,
* displays a raised surface with a subtle border to indicate the active state.
* Unpressed items have a transparent background with secondary text color.
*
* @param props - Toggle item configuration including icon and label
* @returns The rendered toggle item button
*/
function ToggleGroupItem({ className, children, icon, label, ...props }) {
	const variant = React.useContext(ToggleGroupVariantContext);
	return /* @__PURE__ */ jsxs(Toggle, {
		className: cn("flex cursor-pointer items-center", "text-body-medium leading-body-medium font-heading-medium", "outline-none transition-colors duration-150", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-text-selected", variant === "default" && [
			"flex-1 flex-col gap-[2px] rounded-xs px-md py-xs",
			"data-[pressed]:border-(length:--border-width-default) data-[pressed]:border-solid data-[pressed]:border-border-primary data-[pressed]:bg-elevation-surface-raised",
			"data-[pressed]:text-text-primary data-[pressed]:shadow-elevation-element",
			"not-data-[pressed]:border-(length:--border-width-default) not-data-[pressed]:border-solid not-data-[pressed]:border-transparent not-data-[pressed]:bg-transparent",
			"not-data-[pressed]:text-text-secondary",
			"not-data-[pressed]:hover:bg-background-neutral-secondary-hover"
		], variant === "pill" && [
			"shrink-0 flex-row justify-center gap-xxs rounded-[var(--radius-round)] px-md py-sm",
			"border-(length:--border-width-bold) border-solid bg-background-neutral-primary text-text-primary shadow-elevation-element",
			"data-[pressed]:border-border-focused",
			"not-data-[pressed]:border-border-primary",
			"not-data-[pressed]:hover:bg-background-neutral-primary-hover",
			"data-[disabled]:not-data-[pressed]:border-border-disabled data-[disabled]:not-data-[pressed]:bg-background-disabled"
		], "data-[disabled]:cursor-not-allowed data-[disabled]:text-text-disabled data-[disabled]:[&_svg]:text-icon-disabled", "data-[disabled]:data-[pressed]:bg-background-disabled data-[disabled]:data-[pressed]:border-border-disabled data-[disabled]:data-[pressed]:text-text-disabled data-[disabled]:data-[pressed]:shadow-none", variant === "default" && "data-[disabled]:not-data-[pressed]:bg-transparent", "data-[disabled]:not-data-[pressed]:hover:bg-transparent", className),
		...props,
		children: [icon && /* @__PURE__ */ jsx("span", {
			className: "flex size-[16px] items-center justify-center [&>svg]:size-full",
			children: icon
		}), label ?? children]
	});
}
//#endregion
export { ToggleGroup, ToggleGroupItem };

//# sourceMappingURL=ToggleGroup.js.map